import type { SxProps, Theme } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../../assets/styles/themes/tokens';
import { aiAssistantEditorContext } from '../../../../state/AIAssistantEditorProvider';
import { notifyBugsnagHandledError } from '../../../../utils/bugsnagClient';
import { copyElementContent } from '../../../../utils/copyElementContent';
import AIAssistantIconButton from '../../Dialog/AIAssistantIconButton';

export const COPY_BUTTON_TEST_ID = 'text-editor-copy-button';

interface AIAssistantTextEditorCopyButtonProps {
	content?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
}

const AIAssistantTextEditorCopyButton: React.FC<AIAssistantTextEditorCopyButtonProps> = ({
	content: contentOverride,
	dataTestId = COPY_BUTTON_TEST_ID,
	sx,
}) => {
	const { t } = useTranslation();
	const { editorData } = useContext(aiAssistantEditorContext);
	const [isCopied, setIsCopied] = useState(false);

	const content = contentOverride ?? editorData.content;

	const handleCopy = async () => {
		try {
			const temp = document.createElement('div');
			temp.innerHTML = content;
			await copyElementContent(temp);

			setIsCopied(true);

			// Reset icon after 5 seconds
			setTimeout(() => {
				setIsCopied(false);
			}, 5000);
		} catch (error) {
			notifyBugsnagHandledError(error as Error);
		}
	};

	const canCopy = Boolean(content);

	return (
		<AIAssistantIconButton
			data-testid={dataTestId}
			icon={isCopied ? 'check' : 'copy'}
			tooltip={t('Copy to clipboard')}
			onClick={handleCopy}
			disabled={!canCopy}
			sx={isCopied ? { color: palette.success.main } : sx}
		/>
	);
};

export default AIAssistantTextEditorCopyButton;
